import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Circle, Mail, Globe, Check, Layers, Target } from 'lucide-react';
import i18n from '../i18n';
import Footer from '../components/Footer';

const RegisterPage = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [uiLanguage, setUiLanguage] = useState(localStorage.getItem('language') || 'en');

  // Hide Emergent badge on register page
  useEffect(() => {
    document.body.classList.remove('show-emergent-badge');
    return () => {
      // Keep it hidden
    };
  }, []);

  const handleLanguageChange = (lang) => {
    setUiLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await register(name, email, password, uiLanguage);
      
      if (result?.needsConfirmation) {
        setEmailSent(true);
        toast.info(result.message);
        return;
      }
      
      toast.success(t('register_success') || 'Registrazione completata!');
      navigate('/dashboard');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Registrazione fallita';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Email confirmation screen
  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Controlla la tua email</CardTitle>
            <CardDescription className="text-base">
              Abbiamo inviato un link di conferma a <strong>{email}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500 text-center">
              Clicca sul link nell'email per attivare il tuo account. 
              Se non trovi l'email, controlla la cartella spam.
            </p>
            <div className="flex flex-col gap-2">
              <Button 
                variant="outline" 
                onClick={() => setEmailSent(false)}
                className="w-full"
              >
                Usa un'altra email
              </Button>
              <Link to="/login">
                <Button variant="ghost" className="w-full">
                  Torna al login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left - Form */}
      <div className="flex items-center justify-center p-8 bg-white relative">
        {/* Language selector - top right */}
        <div className="absolute top-4 right-4">
          <Select value={uiLanguage} onValueChange={handleLanguageChange}>
            <SelectTrigger className="w-[90px] h-8 text-xs" data-testid="language-selector">
              <Globe className="w-3 h-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="it">Italiano</SelectItem>
              <SelectItem value="de">Deutsch</SelectItem>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="zh">中文</SelectItem>
              <SelectItem value="hi">हिंदी</SelectItem>
              <SelectItem value="ar">عربي</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-full max-w-md space-y-8">
          {/* Logo: sMartBAlloon - M and B in blue, Al in red (AI + initials MB) */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                <Circle className="w-6 h-6 text-white" strokeWidth={3} />
              </div>
              <span className="text-2xl font-black tracking-tight text-slate-900">
                S<span className="text-blue-600">M</span>art<span className="text-blue-600">B</span><span className="text-red-500">Al</span>loon
              </span>
            </div>
            <p className="text-xs font-semibold text-primary uppercase tracking-widest pl-[52px]">
              Built by engineers, for engineers
            </p>
          </div>

          <Card className="border-slate-200 shadow-none">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold">{t('register')}</CardTitle>
              <CardDescription>
                {t('have_account')} <Link to="/login" className="text-primary hover:underline">{t('login')}</Link>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="dense-label">{t('name')}</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Mario Rossi"
                    required
                    className="dense-input"
                    data-testid="register-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="dense-label">{t('email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@azienda.com"
                    required
                    className="dense-input"
                    data-testid="register-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="dense-label">{t('password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="dense-input"
                    data-testid="register-password-input"
                  />
                  <p className="text-xs text-slate-400">Minimo 6 caratteri</p>
                </div>
                
                <div className="flex items-start space-x-2">
                  <Checkbox 
                    id="terms" 
                    checked={acceptTerms}
                    onCheckedChange={setAcceptTerms}
                    data-testid="accept-terms-checkbox"
                  />
                  <label 
                    htmlFor="terms" 
                    className="text-xs text-slate-600 leading-tight cursor-pointer"
                  >
                    {uiLanguage === 'it' ? (
                      <>
                        Accetto i{' '}
                        <Link to="/terms" className="text-primary hover:underline" target="_blank">
                          Termini di Servizio
                        </Link>
                        {' '}e la{' '}
                        <Link to="/privacy" className="text-primary hover:underline" target="_blank">
                          Privacy Policy
                        </Link>
                      </>
                    ) : uiLanguage === 'de' ? (
                      <>
                        Ich akzeptiere die{' '}
                        <Link to="/terms" className="text-primary hover:underline" target="_blank">
                          Nutzungsbedingungen
                        </Link>
                        {' '}und die{' '}
                        <Link to="/privacy" className="text-primary hover:underline" target="_blank">
                          Datenschutzrichtlinie
                        </Link>
                      </>
                    ) : uiLanguage === 'fr' ? (
                      <>
                        J'accepte les{' '}
                        <Link to="/terms" className="text-primary hover:underline" target="_blank">
                          Conditions d'Utilisation
                        </Link>
                        {' '}et la{' '}
                        <Link to="/privacy" className="text-primary hover:underline" target="_blank">
                          Politique de Confidentialité
                        </Link>
                      </>
                    ) : (
                      <>
                        I accept the{' '}
                        <Link to="/terms" className="text-primary hover:underline" target="_blank">
                          Terms of Service
                        </Link>
                        {' '}and{' '}
                        <Link to="/privacy" className="text-primary hover:underline" target="_blank">
                          Privacy Policy
                        </Link>
                      </>
                    )}
                  </label>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={loading || !acceptTerms}
                  data-testid="register-submit-btn"
                >
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {t('register')}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* LSC Credit */}
          <div className="text-center pt-4">
            <p className="text-sm text-slate-500 inline-flex items-center gap-2">
              <span>{t('created_by_experience')}</span>
              <a 
                href="https://www.lscsrl.it" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center hover:opacity-80 transition-opacity"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_quality-balloon-1/artifacts/uqnom16q_LOGO.jpg" 
                  alt="L.S.C. s.r.l." 
                  className="h-6 align-middle"
                />
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Right - Hero Section */}
      <div 
        className="hidden lg:block bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1769152683420-f4eff91cb30b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxlbmdpbmVlcmluZyUyMGJsdWVwcmludHxlbnwwfHx8fDE3NzQ1NTg5NDR8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        {/* Dark overlay for better contrast */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-800/85 to-slate-900/90" />
        
        <div className="absolute inset-0 flex items-center justify-center p-8 overflow-y-auto">
          <div className="text-center space-y-6 max-w-xl py-8">
            {/* Main Headline */}
            <div className="space-y-3">
              <p className="text-xs font-semibold text-blue-400 uppercase tracking-[0.2em]">
                Built by engineers, for engineers
              </p>
              <h1 className="text-3xl lg:text-4xl font-black text-white leading-tight">
                {t('hero_title')}
              </h1>
              <p className="text-lg text-slate-300 font-light">
                {t('hero_subtitle')}
              </p>
            </div>
            
            {/* 3-Step Flow */}
            <div className="flex items-center justify-center gap-2 text-xs">
              <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">1</div>
                <span className="text-white font-medium">{t('hero_step1')}</span>
              </div>
              <div className="w-4 h-px bg-white/30"></div>
              <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">2</div>
                <span className="text-white font-medium">{t('hero_step2')}</span>
              </div>
              <div className="w-4 h-px bg-white/30"></div>
              <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold">3</div>
                <span className="text-white font-medium">{t('hero_step3')}</span>
              </div>
            </div>
            
            {/* Feature Cards Container */}
            <div className="space-y-4 pt-2">
              
              {/* SmartBalloon Card (PRIMARY - Larger) */}
              <div className="bg-gradient-to-br from-blue-600/40 via-blue-500/30 to-cyan-500/30 backdrop-blur-md border border-blue-400/40 rounded-2xl p-5 shadow-2xl shadow-blue-900/30">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">{t('smartballoon_title')}</h3>
                    <p className="text-blue-100 text-sm leading-relaxed">
                      {t('smartballoon_subtitle')}
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-blue-400/30">
                  <div className="grid grid-cols-3 gap-2 text-xs text-blue-100">
                    <span className="flex items-center gap-1.5">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>{t('smartballoon_feature1')}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>{t('smartballoon_feature2')}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>{t('smartballoon_feature3')}</span>
                    </span>
                  </div>
                </div>
              </div>
              
              {/* SmartRevision Section */}
              <div className="space-y-3">
                {/* Headline above card */}
                <div className="text-center">
                  <p className="text-lg font-bold text-amber-300 italic">
                    "{t('smartrevision_headline')}"
                  </p>
                  <p className="text-sm text-slate-300 mt-1">
                    {t('smartrevision_subheadline')}
                  </p>
                </div>
                
                {/* SmartRevision Card */}
                <div className="bg-gradient-to-br from-purple-600/35 via-indigo-600/30 to-violet-600/35 backdrop-blur-md border border-purple-400/40 rounded-2xl p-5 shadow-2xl shadow-purple-900/30 relative overflow-hidden">
                  {/* Glow effect */}
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl"></div>
                  
                  <div className="flex items-start gap-4 relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Layers className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left flex-1">
                      <h3 className="text-lg font-bold text-white mb-1">{t('smartrevision_title')}</h3>
                      <p className="text-purple-200 text-sm leading-relaxed">
                        {t('smartrevision_subtitle')}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-purple-400/30 relative z-10">
                    <div className="grid grid-cols-3 gap-2 text-xs text-purple-100">
                      <span className="flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span>{t('smartrevision_feature1')}</span>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span>{t('smartrevision_feature2')}</span>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span>{t('smartrevision_feature3')}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* AI Premium Badge */}
            <p className="text-xs text-slate-400">
              {t('hero_ai_premium')} <span className="text-purple-300 font-semibold">AI Premium</span>
            </p>
          </div>
        </div>
      </div>

      {/* Footer with legal links */}
      <div className="lg:col-span-2">
        <Footer />
      </div>
    </div>
  );
};

export default RegisterPage;
